import { useState, useEffect } from 'react';
import StorageService from '../services/storageService';
import { KEYS } from '../services/initService';
import { DEFAULT_HERO_IMAGES } from '../data/heroImages';

export default function Hero({ onMenu }) {
  const [imgIdx, setImgIdx] = useState(0);
  const [heroImages, setHeroImages] = useState(DEFAULT_HERO_IMAGES);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const stored = await StorageService.get(KEYS.HERO_IMAGES);
      if (!mounted) return;
      if (Array.isArray(stored) && stored.length > 0) {
        const cleaned = stored
          .slice(0, 5)
          .map((img, index) => ({
            url: String(img?.url || '').trim(),
            label: String(img?.label || `Hero ${index + 1}`).trim() || `Hero ${index + 1}`,
          }))
          .filter((img) => img.url);
        if (cleaned.length > 0) setHeroImages(cleaned);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // Cycle hero images every 6 seconds
  useEffect(() => {
    if (imgIdx >= heroImages.length) setImgIdx(0);
    const t = setInterval(() => {
      setImgIdx((i) => (i + 1) % heroImages.length);
    }, 6000);
    return () => clearInterval(t);
  }, [heroImages.length, imgIdx]);

  return (
    <section
      id="home"
      style={{
        minHeight: '100vh',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        background: 'var(--dark)',
      }}
    >
      {/* Background image */}
      {heroImages.map((img, i) => (
        <div
          key={img.url}
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url(${img.url})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center 40%',
            opacity: i === imgIdx ? 1 : 0,
            transition: 'opacity 1.2s ease',
            zIndex: 0,
          }}
          aria-hidden="true"
        />
      ))}

      {/* Gradient overlay */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(105deg, rgba(9,64,77,0.91) 0%, rgba(13,30,34,0.75) 50%, rgba(13,30,34,0.35) 100%)',
          zIndex: 1,
        }}
      />

      {/* Decorative glow */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          width: 500,
          height: 500,
          background: 'radial-gradient(circle, rgba(26,122,138,0.25) 0%, transparent 70%)',
          top: -80,
          right: '10%',
          zIndex: 1,
          animation: 'pulseGlow 4.5s ease-in-out infinite',
        }}
      />

      {/* Content */}
      <div
        style={{
          position: 'relative',
          zIndex: 2,
          maxWidth: 'var(--max-width)',
          margin: '0 auto',
          padding: 'calc(var(--nav-top-inset) + var(--nav-height) + 3rem) 2rem 4rem',
          width: '100%',
        }}
      >
        <div style={{ maxWidth: 640 }}>
          {/* Badge */}
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '.5rem',
              background: 'rgba(201,146,42,0.15)',
              border: '1px solid rgba(201,146,42,0.45)',
              color: 'var(--gold-light)',
              padding: '.38rem 1rem',
              borderRadius: 'var(--radius-pill)',
              fontSize: 'var(--text-label-medium-size)',
              fontWeight: 700,
              letterSpacing: '.1em',
              textTransform: 'uppercase',
              marginBottom: '1.5rem',
              animation: 'fadeInUp .7s ease both',
            }}
          >
            🇹🇭 Authentic Thai, Beau Vallon Seychelles
          </div>

          <h1
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'clamp(2.6rem, 6vw, 4.2rem)',
              lineHeight: 1.08,
              color: 'white',
              marginBottom: '1.1rem',
              animation: 'fadeInUp .7s .08s ease both',
            }}
          >
            Order Now,<br />
            <span style={{ color: 'var(--gold-light)', fontStyle: 'italic' }}>Skip the Wait.</span>
          </h1>

          <p
            style={{
              color: 'rgba(255,255,255,0.7)',
              fontSize: 'var(--text-body-large-size)',
              fontWeight: 300,
              lineHeight: 'var(--text-body-large-line)',
              marginBottom: '2.25rem',
              animation: 'fadeInUp .7s .16s ease both',
              maxWidth: 520,
            }}
          >
            Pay online, arrive at the kiosk and collect your fresh Thai food instantly. Zero waiting time, maximum flavour.
          </p>

          {/* CTAs */}
          <div
            className="hero-cta-row"
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '1rem',
              marginBottom: '2rem',
              animation: 'fadeInUp .7s .24s ease both',
            }}
          >
            <button className="btn btn-outline" onClick={onMenu} style={{ fontSize: '1rem' }}>
              View Full Menu
            </button>
            <a
              href="#specials"
              className="btn btn-gold"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('specials')?.scrollIntoView({ behavior: 'smooth' });
              }}
              style={{ fontSize: '1rem' }}
            >
              Today&apos;s Specials
            </a>
          </div>
        </div>

        {/* Image indicator dots */}
        <div
          style={{
            position: 'absolute',
            bottom: '2rem',
            right: '2rem',
            display: 'flex',
            gap: '.5rem',
          }}
          aria-hidden="true"
        >
          {heroImages.map((_, i) => (
            <button
              key={i}
              onClick={() => setImgIdx(i)}
              style={{
                width: i === imgIdx ? 28 : 8,
                height: 8,
                borderRadius: 4,
                background: i === imgIdx ? 'var(--gold-light)' : 'rgba(255,255,255,0.3)',
                border: 'none',
                transition: 'all .35s ease',
                padding: 0,
              }}
              aria-label={`Show ${heroImages[i].label}`}
            />
          ))}
        </div>
      </div>

      <style>{`
        @media (max-width: 560px) {
          .hero-cta-row {
            flex-direction: column !important;
            align-items: center !important;
            width: 100%;
          }
          .hero-cta-row .btn {
            width: 100%;
            max-width: 280px;
            justify-content: center;
          }
        }
      `}</style>
    </section>
  );
}
